import FileSaver from "file-saver";
import {MONTHS} from "../../../../constants/Months";
import {formatterIntNoGrouping, formatterPercentNoGrouping, formatExportTimestampSlug} from "../../../../utils/formatters";
import {propertyOutlierMetrics} from "./outlierLogic";
import {formatMonthLabel, METRIC_KEYS, METRIC_LABELS, MetricKey, MonthSelection, PropertyDataHealthRow, showsAbsoluteChange, Thresholds} from "./types";

function csvEscape(value: string): string {
    if (value.length === 0) return "";
    const needsQuoting = /[",\n\r]/.test(value);
    const escaped = value.replace(/"/g, '""');
    return needsQuoting ? `"${escaped}"` : escaped;
}

function formatValue(v: number | null | undefined, metric: MetricKey): string {
    if (v == null) return "";
    if (metric === "occupancyPct") {
        return formatterPercentNoGrouping.format(v / 100);
    }
    return formatterIntNoGrouping.format(v);
}

function formatPercentChange(v: number | null | undefined): string {
    if (v == null) return "";
    return formatterPercentNoGrouping.format(v);
}

function slugMonth(m: MonthSelection): string {
    const name = MONTHS[m.monthIndex] ?? "";
    return `${name.toLowerCase()}-${m.year}`;
}

function buildCsv(
    rows: PropertyDataHealthRow[],
    thresholds: Thresholds,
    baseMonth: MonthSelection,
    comparisonMonth: MonthSelection,
): string {
    const baseLabel = formatMonthLabel(baseMonth);
    const cmpLabel = formatMonthLabel(comparisonMonth);

    const headers: string[] = ["Property", "Reforecast start"];
    for (const metric of METRIC_KEYS) {
        const label = METRIC_LABELS[metric];
        headers.push(`${label} (${baseLabel})`);
        headers.push(`${label} (${cmpLabel})`);
        if (showsAbsoluteChange(metric)) {
            headers.push(`${label} (Change)`);
        }
        headers.push(`${label} (Change %)`);
    }
    headers.push("Flagged metrics");

    const lines: string[] = [headers.map(csvEscape).join(",")];

    for (const row of rows) {
        const flaggedMetrics = propertyOutlierMetrics(row, thresholds);
        const flagged = flaggedMetrics.map(m => METRIC_LABELS[m]).join("; ");

        const propertyCell = flaggedMetrics.length > 0
            ? `${row.propertyName} (Outlier)`
            : row.propertyName;

        const reforecastStartCell = formatMonthLabel({
            year: row.reforecastYear,
            monthIndex: row.reforecastStartMonthIndex,
        });

        const cells: string[] = [propertyCell, reforecastStartCell];
        for (const metric of METRIC_KEYS) {
            const m = row[metric];
            cells.push(formatValue(m.base, metric));
            cells.push(formatValue(m.comparison, metric));
            if (showsAbsoluteChange(metric)) {
                cells.push(formatValue(m.absoluteChange, metric));
            }
            cells.push(formatPercentChange(m.percentChange));
        }
        cells.push(flagged);

        lines.push(cells.map(csvEscape).join(","));
    }

    return lines.join("\n");
}

export function exportDataHealthCsv(
    rows: PropertyDataHealthRow[],
    thresholds: Thresholds,
    baseMonth: MonthSelection,
    comparisonMonth: MonthSelection,
): void {
    const csv = buildCsv(rows, thresholds, baseMonth, comparisonMonth);
    const blob = new Blob([csv], {type: "text/csv;charset=utf-8"});
    FileSaver.saveAs(blob, `data_health_${slugMonth(baseMonth)}_vs_${slugMonth(comparisonMonth)}_${formatExportTimestampSlug(new Date())}.csv`);
}
